/*
  Which panel sections this person keeps open.

  THE SIDE PANEL SHOWS EVERYTHING IT HAS, ALWAYS. `PersonPanel` renders ten sections into a column
  that starts at 320px, `GroupPanel` seven, `DataPanel` six, and `.side-panel` is one `overflow-y:
  auto` — so reaching the ninth thing means scrolling past the eight you were not looking for. Every
  section earns its place SOMETIMES; nothing expressed which, and "sometimes" is the whole problem.

  WHY `localStorage` AND NOT `user_project_prefs`, since this client keeps browser persistence to
  almost nothing and `ThemeToggle` is the only other exception. The rule that file states is that
  project state lives on the server and per-user VIEW state lives in the prefs row, because both must
  survive a new machine and be shared or audited. A collapsed set is neither:

   · It is a WORKING HABIT, not a fact about an engagement. "I never look at stranded cost" is true of
     the person in every project they open. In `user_project_prefs` — keyed `(user, project)` — they
     would collapse the same six sections again in the next project, and the one after that.
   · It carries no engagement data. The keys are section names this repository chose; there is no
     roster, no decision and nothing anybody would audit.
   · It is read before the first paint, like the theme. A server round trip to decide what to draw is
     the thing that makes a panel flash open and then shut.

  ONLY EXPLICIT CHOICES ARE STORED, WHICH IS THE ONE THING A NAIVE VERSION GETS WRONG. A section
  nobody has touched has no entry at all, so it follows whatever the call site currently declares.
  Write the defaults in too and the map freezes today's policy onto every existing user: the day
  somebody argues a section should open by default, everybody who has been using the product would be
  the only people who never see the change.
*/
import { useSyncExternalStore } from 'react';

const KEY = 'integrate:sections';

/** Explicit choices only. An id that is absent has never been touched — see the header. */
type Choices = Record<string, boolean>;

function read(): Choices {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // Believing as little as possible: this is a string somebody could have edited by hand, and a
    // non-boolean value here would make `?? defaultOpen` return the wrong branch rather than throw.
    const out: Choices = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') out[id] = value;
    }
    return out;
  } catch {
    // Private browsing, a full quota, or malformed JSON. Everything follows its default this session.
    return {};
  }
}

let choices: Choices = read();

/*
  What is on screen RIGHT NOW, and what each of those declared as its default.

  THE DEFAULT HAS TO TRAVEL WITH THE ID, because "how many of the sections in front of me are open"
  cannot be answered from `choices` alone — the untouched ones are deliberately absent from it. Without
  this the control would read an untouched panel as entirely closed and offer to expand what is
  already open.

  Not persisted: it is a fact about this render rather than a preference.
*/
const mounted = new Map<string, boolean>();

const listeners = new Set<() => void>();
function emit(): void {
  for (const listener of listeners) listener();
}

function persist(): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(choices));
  } catch {
    // The choice still holds for this session; only remembering it failed. A panel must not go down
    // over a disclosure, which is why every touch of `localStorage` in this file is wrapped.
  }
}

/**
 * Subscribe to the whole store.
 *
 * ONE SUBSCRIPTION LIST FOR EVERY SECTION rather than one per id: `useSyncExternalStore` re-reads the
 * snapshot on notify and only re-renders where the snapshot actually changed, so the extra reads cost
 * a boolean comparison each and the alternative is a map of listener sets to keep in step.
 */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Is this section open?
 *
 * The snapshot is a BOOLEAN — a primitive — so there is no cached-object trap here. Returning a fresh
 * object from `getSnapshot` is the standard way to make `useSyncExternalStore` loop forever, and the
 * reason this reads one id at a time rather than handing out the map.
 */
export function useSectionOpen(id: string, defaultOpen: boolean): boolean {
  return useSyncExternalStore(
    subscribe,
    () => choices[id] ?? defaultOpen,
    // Server snapshot: there is no SSR here, but the argument is required and the honest answer is
    // the default, since a server has no browser storage to read.
    () => defaultOpen,
  );
}

/** Open or close one section, and remember that this was a choice rather than a default. */
export function setSection(id: string, open: boolean): void {
  choices = { ...choices, [id]: open };
  persist();
  emit();
}

/** Register a section as being on screen. Returns the matching unregister, so it is an effect body. */
export function registerSection(id: string, defaultOpen: boolean): () => void {
  mounted.set(id, defaultOpen);
  emit();
  return () => {
    mounted.delete(id);
    emit();
  };
}

/**
 * Open or close everything CURRENTLY ON SCREEN.
 *
 * Not everything ever seen. "Collapse all" means the panel in front of you — a control that also
 * closed forty sections in five other panels would be a control nobody could predict, and the person
 * pressing it is looking at one panel.
 */
export function setAllMounted(open: boolean): void {
  if (mounted.size === 0) return;
  const next = { ...choices };
  for (const id of mounted.keys()) next[id] = open;
  choices = next;
  persist();
  emit();
}

/*
  TWO HOOKS RETURNING NUMBERS, NOT ONE RETURNING A TALLY OBJECT.

  `getSnapshot` runs on every render and React bails out only on `Object.is`. A fresh `{ total, open }`
  is never `Object.is` to the last one, so the hook would re-render, recompute, and re-render — the
  standard way to make `useSyncExternalStore` loop forever. Every snapshot in this file is a primitive
  for that reason.
*/

/** How many sections are on screen. */
export function useMountedCount(): number {
  return useSyncExternalStore(subscribe, () => mounted.size, () => 0);
}

/** How many of those are open, counting an untouched one as whatever its call site declared. */
export function useOpenCount(): number {
  const count = (): number => {
    let open = 0;
    for (const [id, defaultOpen] of mounted) if (choices[id] ?? defaultOpen) open += 1;
    return open;
  };
  return useSyncExternalStore(subscribe, count, () => 0);
}

/*
  A SECOND BROWSER TAB FOLLOWS THIS ONE. `storage` fires in every OTHER tab of the same origin when
  one of them writes, which is the literal reading of "remembered across tabs" — without it, two tabs
  drift apart for as long as both stay open and the loser is whichever writes last.

  Registered once at module load rather than per component: forty sections mounting would otherwise
  attach forty listeners for one event that concerns the whole store.
*/
try {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== KEY) return;
    choices = read();
    emit();
  });
} catch {
  // No `window` (a test environment that stubs it away). Everything still works within this tab.
}

/** Test seam: forget every choice, as if nobody had ever touched a section. */
export function resetSections(): void {
  choices = {};
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to forget */
  }
  emit();
}
